/**
 * Infrastructure: what Arcanum's AWS accounts with no Numa stack cost. CI,
 * image hosting, the deployer, the organisation roots, the account pool.
 *
 * These accounts carry an AWS bill and nothing else: no chat, no people, no
 * credits. So the page is the bill, as AWS billed it, by day, by service and
 * by account. It follows the selection: the Infrastructure view shows every
 * such account, and one account shows that account alone.
 */
import { useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import type { ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import '../chartSetup';
import { CopyButton } from '@/components/ui/CopyButton';
import { ND_COLORS, ND_FILL } from '@/styles/theme';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { useCurrency } from '../currencyContext';
import { isReady, selectAccountRows, selectBillByService, selectSeries, shareOf } from '../selectors';
import type { AccountRow } from '../selectors';
import { fmtUSD, poolForAggregate } from '../shared';
import { Empty, Kpi, KpiGrid, NeedsTier, Panel, Section } from '../ui/kit';
import {
  Bar,
  dayLabel,
  FreshnessMark,
  gridCols,
  LinkButton,
  moneyScale,
  plural,
  Quiet,
  SearchBox,
  sortRows,
  SortTh,
  useSort,
} from './common';
import type { SortValue } from './common';
import type { DashboardTabProps } from './registry';
import './Infrastructure.css';

const fmtShare = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

/** A share as a number with a bar under it, so a long list can be read at a glance. */
function ShareCell({ fraction }: { fraction: number }) {
  return (
    <td className="numeric text-end infra-share">
      {fmtShare(fraction)}
      <Bar fraction={fraction} className="dash-bar--under" />
    </td>
  );
}

type SortKey = 'account' | 'bill';
const WORD_COLUMNS: ReadonlySet<SortKey> = new Set<SortKey>(['account']);
const SORT_VALUE: Record<SortKey, (r: AccountRow) => SortValue> = {
  account: (r) => r.label,
  bill: (r) => r.awsBill,
};

export function Infrastructure({ data, window: win, snapshots, fleet, selection, onSelectClient }: DashboardTabProps) {
  useCurrency();
  const [query, setQuery] = useState('');
  const { sort, onSort } = useSort<SortKey>({ key: 'bill', dir: 'desc' }, WORD_COLUMNS);

  const accounts = useMemo(() => selectAccountRows(selection.stacks, win), [selection.stacks, win]);
  // Everything Arcanum pays AWS for, to say how large a part of it this selection is.
  const arcanum = useMemo(() => selectAccountRows(poolForAggregate(snapshots, 'arcanum'), win), [snapshots, win]);
  const services = useMemo(() => selectBillByService(accounts.rows), [accounts.rows]);
  const daily = useMemo(() => selectSeries(data, win, 'awsBill'), [data, win]);

  const single = accounts.rows.length === 1 ? accounts.rows[0] : null;
  const largest = accounts.rows[0];
  const share = shareOf(accounts.total, arcanum.total);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kept = accounts.rows.filter((r) => !q || r.label.toLowerCase().includes(q) || r.accountId.includes(q));
    // Within one value of the sorted column, the accounts are in order of their names.
    return sortRows(sortRows(kept, SORT_VALUE.account, 'asc'), SORT_VALUE[sort.key], sort.dir);
  }, [accounts.rows, query, sort]);

  if (accounts.rows.length === 0) {
    return <Empty>{selection.label} holds no AWS account to show.</Empty>;
  }

  const days = isReady(daily) ? daily.days : [];
  const chartOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      // One line, named by the panel's title.
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => fmtUSD(Number(ctx.parsed.y)) } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 } },
      y: moneyScale(),
    },
  };

  const singleFreshness = single ? fleet.freshness(single.client) : null;

  const tiles =
    1 +
    (share !== null ? 1 : 0) +
    (!single && largest && largest.awsBill > 0 ? 1 : 0) +
    (services === null || services.length > 0 ? 1 : 0);

  return (
    <div className="infra">
      <Section
        title="AWS bill"
        note={
          single
            ? 'An Arcanum AWS account with no Numa stack. The bill is shown as AWS billed it.'
            : 'Arcanum AWS accounts with no Numa stack. The bill is shown as AWS billed it.'
        }
      >
        <KpiGrid cols={gridCols(tiles)}>
          <Kpi
            lead
            label="AWS bill"
            value={fmtUSD(accounts.total)}
            sub={single ? single.label : plural(accounts.rows.length, 'account')}
            hint="What AWS billed in the window. Nothing is moved in or out of it: these accounts run no chat."
          />
          {share !== null && (
            <Kpi
              label="Share of Arcanum's AWS bill"
              value={fmtShare(share)}
              sub={`of ${fmtUSD(arcanum.total)}`}
              hint="This bill as a share of the AWS bill of every account Arcanum pays for: NextGen, internal and infrastructure accounts, each counted once."
            />
          )}
          {!single && largest && largest.awsBill > 0 && (
            <Kpi
              label="Largest account"
              value={largest.label}
              sub={`${fmtUSD(largest.awsBill)}, ${fmtShare(largest.share)} of the bill`}
            />
          )}
          {services === null ? (
            <Kpi label="Largest service" value="…" />
          ) : (
            services.length > 0 && (
              <Kpi
                label="Largest service"
                value={services[0].service}
                sub={`${fmtUSD(services[0].usd)}, ${fmtShare(services[0].share)} of the bill`}
              />
            )
          )}
        </KpiGrid>

        <div className="dash-cols infra-pair">
          <Panel title="AWS bill by day" subtitle="The bill stops a day or two before today.">
            {isReady(daily) && daily.available && daily.values.some((v) => v > 0) ? (
              <div className="dash-chart">
                <Line
                  data={{
                    labels: days.map(dayLabel),
                    datasets: [
                      {
                        label: 'AWS bill',
                        data: daily.values,
                        borderColor: ND_COLORS.accent,
                        backgroundColor: ND_FILL.accent,
                        fill: true,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        tension: 0.25,
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              </div>
            ) : (
              <Empty>No AWS bill in this window.</Empty>
            )}
          </Panel>

          <Panel title="By service" flush>
            <NeedsTier fleet={fleet} tiers={['services']} what="Spend by service">
              {services === null ? (
                <Empty>Loading spend by service</Empty>
              ) : services.length === 0 ? (
                <Empty>No AWS bill in this window.</Empty>
              ) : (
                <div className="dash-scroll dash-scroll--short">
                  <Table className="dash-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th className="text-end">AWS bill</th>
                        <th className="text-end">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((s) => (
                        <tr key={s.service}>
                          <td className="dash-name">{s.service}</td>
                          <td className="numeric text-end">{fmtUSD(s.usd)}</td>
                          <ShareCell fraction={s.share} />
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </NeedsTier>
          </Panel>
        </div>
      </Section>

      {single ? (
        <Panel title="About this account">
          <dl className="infra-facts">
            <dt>AWS account</dt>
            <dd>
              <span className="mono">{single.accountId || 'not recorded'}</span>
              {single.accountId && <CopyButton value={single.accountId} label="Copy the AWS account id" />}
            </dd>
            {single.payerAccountId && (
              <>
                <dt>Billed through</dt>
                <dd>
                  <span className="mono">{single.payerAccountId}</span>
                  <CopyButton value={single.payerAccountId} label="Copy the payer account id" />
                </dd>
              </>
            )}
            <dt>Last updated</dt>
            <dd>
              <span title={formatNzDateTime(singleFreshness?.generatedAt ?? single.generatedAt)}>
                {timeAgo(singleFreshness?.generatedAt ?? single.generatedAt) || 'not recorded'}
              </span>
              {singleFreshness && (
                <FreshnessMark
                  generatedAt={singleFreshness.generatedAt}
                  stale={singleFreshness.stale}
                  failed={singleFreshness.failed}
                  error={singleFreshness.error}
                />
              )}
            </dd>
          </dl>
        </Panel>
      ) : (
        <Panel
          title="Accounts"
          subtitle={`${plural(accounts.rows.length, 'account')}. Select an account to see it on its own.`}
          flush
          actions={
            <SearchBox
              value={query}
              onChange={setQuery}
              placeholder="Search accounts"
              label="Search accounts by name or AWS account id"
            />
          }
        >
          <div className="dash-scroll dash-scroll--tall">
            <Table hover className="dash-table">
              <thead>
                <tr>
                  <SortTh column="account" sort={sort} onSort={onSort}>
                    Account
                  </SortTh>
                  <th>AWS account</th>
                  <SortTh column="bill" sort={sort} onSort={onSort} numeric>
                    AWS bill
                  </SortTh>
                  <th className="text-end">Share</th>
                  <th>Largest service</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <Empty>No account matches the search.</Empty>
                    </td>
                  </tr>
                ) : (
                  shown.map((r) => {
                    const fresh = fleet.freshness(r.client);
                    const top = r.services?.[0];
                    return (
                      <tr key={r.client} className="dash-row" onClick={() => onSelectClient(r.client)}>
                        <td>
                          <div className="infra-name">
                            {/* The row is the click target. The button gives it to the keyboard. */}
                            <LinkButton name title={`Open ${r.label}`}>
                              {r.label}
                            </LinkButton>
                            <FreshnessMark
                              generatedAt={fresh.generatedAt}
                              stale={fresh.stale}
                              failed={fresh.failed}
                              error={fresh.error}
                            />
                          </div>
                        </td>
                        <td>
                          {/* Copying an id must not open the account. */}
                          <span className="infra-id" onClick={(e) => e.stopPropagation()}>
                            <span className="mono">{r.accountId}</span>
                            {r.accountId && <CopyButton value={r.accountId} label="Copy the AWS account id" />}
                          </span>
                        </td>
                        <td className="numeric text-end">{fmtUSD(r.awsBill)}</td>
                        <ShareCell fraction={r.share} />
                        <td>
                          {r.services === null ? (
                            <Quiet title="Loading">…</Quiet>
                          ) : top ? (
                            <>
                              {top.service}
                              <span className="dash-sub infra-sub numeric">{fmtUSD(top.usd)}</span>
                            </>
                          ) : (
                            <Quiet title="No AWS bill in this window.">none</Quiet>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Panel>
      )}
    </div>
  );
}
